import React, { useState, useEffect, useContext } from "react";
import axios, { AxiosError } from "axios";
import { Card, Col, Row, Container, Accordion } from "react-bootstrap";
import { api } from "../../API";
import { useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { config } from "../../config";
import { Table } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import useHandleUnauthorized from "../../hooks/useHandleUnauthorized/UseHandleUnauthorized";
import { AppContext } from "../../AppContext/Context";
import Loader from "../loader";
import Select, { SingleValue } from "react-select";
import { useNavigate } from "react-router-dom";
import SweetLayout from "../../Layout/SweetLayout";

interface OptionType {
  label: string;
  value: number;
}

interface KsItem {
  ks_id: number;
  name: string;
  type: string;
  description?: string;
  source_details?: Record<string, any>;
  stats?: Record<string, any>;
  ept_config?: Record<string, any>;
  versions?: any[];
  [key: string]: any;
}

interface KscOwner {
  ksc_id?: number;
  name?: string;
  description?: string;
  confidential?: boolean;
  owner_account_id?: number;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

interface KscResponse {
  kss: KsItem[];
  ksc_id?: number;
  name?: string;
  description?: string;
  confidential?: boolean;
  owner_account_id?: number;
}

interface EditableFields {
  name: boolean;
  url: boolean;
  company_name: boolean;
  confidential: boolean;
  type: boolean;
  owner_account_id: boolean;
  source_details: boolean;
  description: boolean;
}

interface FormData {
  owner_account_id?: number | string;
}

interface Column {
  title: string;
}

const KscDetailedTable: React.FC = () => {
  const [owner, setOwner] = useState<KscOwner>({});
  const nav = useNavigate();
  const [data, setData] = useState<KsItem[]>([]);
  const [ksData, setKsData] = useState<KsItem[]>([]);
  const { id } = useParams<{ id: string }>();
  const [isEditKsDone, setIsEditKsDone] = useState<boolean>(false);
  const [newConfidential, setNewConfidential] = useState<boolean | undefined>(owner.confidential);
  const { isLoading, getIdTokenClaims, user } = useAuth0();
  const [editModeKs, setEditModeKs] = useState<boolean>(false);
  const [editableKs, setEditableKs] = useState<boolean>(false);
  const [selectedKs, setSelectedKs] = useState<OptionType | null>(null);
  const showEditButton = user?.ept_roles?.includes("EptAdmin");
  const { setIsLoad, isLoad } = useContext(AppContext);
  const isActive = data?.length;
  const handleUnauthorized = useHandleUnauthorized();

  const [editableFields, setEditableFields] = useState<EditableFields>({
    name: false,
    url: false,
    company_name: false,
    confidential: false,
    type: false,
    owner_account_id: false,
    source_details: false,
    description: false,
  });

  const [formData, setFormData] = useState<FormData>({
    owner_account_id: owner?.owner_account_id,
  });

  const navigateToKsDetailPage = (ks_id: number) => {
    nav(`/ks/${ks_id}`);
  };

  useEffect(() => {
    if (owner) {
      setNewConfidential(owner.confidential);
    }
  }, [owner]);

  const renderId = (ks_id: number): React.ReactNode => (
    <span
      onClick={() => navigateToKsDetailPage(ks_id)}
      style={{ color: "blue", cursor: "pointer" }}
    >
      <a
        href={`/ks/${ks_id}`}
        onClick={(e) => e.preventDefault()}
        style={{ textDecoration: "none" }}
      >
        <div className="">{ks_id}</div>
      </a>
    </span>
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!isLoading) {
        setIsLoad(true);
        let endpoint = `${config.baseURL}/admin/v1/ks`;
        try {
          const getidToken = await getIdTokenClaims();
          const idToken = getidToken?.__raw;
          const response = await axios.get<KsItem[]>(endpoint, {
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
          });
          const responseData = Array.isArray(response.data)
            ? response.data
            : [response.data];
          setKsData(responseData);
        } catch (error) {
          console.error("API request error:", (error as Error).message);
          handleUnauthorized(error);
        } finally {
          setIsLoad(false);
        }
      }
    };
    fetchData();
  }, [isLoading, getIdTokenClaims, handleUnauthorized, setIsLoad]);

  const options: OptionType[] = ksData.map((data) => ({
    label: `${data.name}(${data.ks_id})`,
    value: data.ks_id,
  }));

  useEffect(() => {
    if (data[0]) {
      setFormData({
        owner_account_id: owner?.owner_account_id,
      });
    }
  }, [data, owner]);

  useEffect(() => {
    if (!isLoading) {
      setIsLoad(true);
      const fetchData = async () => {
        const endpoint = `${config.baseURL}/admin/v1/ksc/${id}`;
        try {
          const getidToken = await getIdTokenClaims();
          const idToken = getidToken?.__raw;
          const response = await axios.get<KscResponse>(endpoint, {
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
          });

          setData(response.data.kss);
          setOwner(response.data);
        } catch (error) {
          console.error("API request error:", (error as Error).message);
          handleUnauthorized(error);
        } finally {
          setIsLoad(false);
        }
      };
      fetchData();
    }
  }, [isLoading, id, getIdTokenClaims, isEditKsDone, handleUnauthorized]);

  const renderAccordion = (key: string, title: string, record: KsItem): React.ReactNode => (
    <Accordion className="mt-4 mb-4" key={key}>
      <Card>
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <strong>{title}</strong>
          </Accordion.Header>
          <Accordion.Body>
            <ul className="d-flex flex-column gap-2">
              <Card.Body>
                {record[key] ? (
                  Object.entries(record[key]).map(
                    ([nestedKey, nestedValue], index) => (
                      <li key={index}>
                        <div>
                          <strong>{nestedKey}</strong>:{" "}
                          {Array.isArray(nestedValue) ? (
                            <Accordion className="mt-2 mb-2">
                              <Accordion.Item eventKey={index.toString()}>
                                <Accordion.Header>{nestedKey}</Accordion.Header>
                                <Accordion.Body>
                                  <ul>
                                    {nestedValue.map((item, idx) => (
                                      <li key={idx}>{typeof item === 'object' ? JSON.stringify(item) : String(item)}</li>
                                    ))}
                                  </ul>
                                </Accordion.Body>
                              </Accordion.Item>
                            </Accordion>
                          ) : (
                            renderNestedValue(nestedValue, nestedKey)
                          )}
                        </div>
                      </li>
                    )
                  )
                ) : (
                  <strong>No Data</strong>
                )}
              </Card.Body>
            </ul>
          </Accordion.Body>
        </Accordion.Item>
      </Card>
    </Accordion>
  );

  const formatDate = (timestamp: number): string => {
    if (!timestamp) return "No Date Provided";
    const date = new Date(timestamp * 1000);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });
  };

  const renderNestedValue = (value: any, key: string): React.ReactNode => {
    if (typeof value === "object" && !Array.isArray(value) && value !== null) {
      return (
        <ul>
          {Object.entries(value).map(([nestedKey, nestedValue], index) => (
            <li key={index}>
              <strong>{nestedKey}</strong>: {renderNestedValue(nestedValue, nestedKey)}
            </li>
          ))}
        </ul>
      );
    } else if (Array.isArray(value)) {
      return null;
    } else {
      return key === "last_updated_date" ? formatDate(value) : String(value);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: string,
    nestedField: string | null = null,
    index: number | null = null
  ) => {
    const { value, checked } = e.target;

    if (nestedField && index !== null) {
      setFormData((prevState) => ({
        ...prevState,
        source_details: {
          ...(prevState as any).source_details,
          [nestedField]: (prevState as any).source_details[nestedField].map((item: any, i: number) =>
            i === index ? value : item
          ),
        },
      }));
    } else if (nestedField) {
      setFormData((prevState) => ({
        ...prevState,
        source_details: {
          ...(prevState as any).source_details,
          [nestedField]: value,
        },
      }));
    } else if (fieldName === "confidential") {
      setFormData((prevState) => ({
        ...prevState,
        [fieldName]: checked,
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [fieldName]: value,
      }));
    }
  };

  const handleEditClick = (fieldName: keyof EditableFields) => {
    setEditableFields((prevState) => ({
      ...prevState,
      [fieldName]: true,
    }));
  };

  const handleSaveClick = async (fieldName: keyof EditableFields) => {
    setEditableFields((prevState) => ({
      ...prevState,
      [fieldName]: false,
    }));
    setIsLoad(true);

    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      const payload = formData;
      await api.put(`/admin/v1/ksc/${id} `, payload, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      setIsLoad(false);
    } catch (error) {
      console.error("Error:", error);
      setIsLoad(false);
    }
  };

  const column: Column[] = [
    {
      title: "Ks-ID",
    },
    { title: "Name" },
    { title: "Type" },
    ...(showEditButton ? [{ title: "Actions" }] : []),
  ];

  let KSpayload = data.map((item) => item.ks_id);

  if (selectedKs && selectedKs.value) {
    KSpayload = [...KSpayload, selectedKs.value];
  }

  const handleRemoveKs = async (ks_id: number) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to remove this item?"
    );
    if (isConfirmed) {
      const filteredPayload = KSpayload.filter((id) => id !== ks_id);

      try {
        const getidToken = await getIdTokenClaims();
        const idToken = getidToken?.__raw;
        await api.put(
          `/admin/v1/ksc/${id}`,
          { ks_ids: filteredPayload },
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          }
        );
        setIsEditKsDone(!isEditKsDone);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setIsLoad(false);
      }
    }
  };

  const handleEditKs = () => {
    setEditModeKs(true);
    setEditableKs(true);
  };

  const hasSameIDs = (arr: number[]): boolean => {
    const idSet = new Set(arr);
    return arr.length !== idSet.size;
  };

  const handleSaveKs = async () => {
    setEditModeKs(false);
    setEditableKs(false);

    if (!selectedKs) {
      return;
    }

    const hasDuplicates = hasSameIDs(KSpayload);

    if (hasDuplicates) {
      alert("This KS is already added.");
      return;
    }

    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;

      await api.put(
        `/admin/v1/ksc/${id}`,
        { ks_ids: KSpayload },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      setIsEditKsDone(!isEditKsDone);
      setIsLoad(false);
    } catch (error) {
      console.error("Error:", error);
      setIsLoad(false);
    } finally {
      setSelectedKs(null);
    }
  };

  const handleKsChange = (selectedOption: SingleValue<OptionType>) => {
    setSelectedKs(selectedOption);
  };

  const handleNewConfidential = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewConfidential(e.target.checked);
  };

  return (
    <SweetLayout>
      <Container fluid>
        {isLoad && (
          <div
            style={{
              position: "absolute",
              top: "150px",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              width: "100%",
            }}
          >
            <Loader />
          </div>
        )}
        <Row>
          <Col>
            <div className="">
              <Row>
                <Col md="6" sm="12">
                  <div>
                    <div
                      onClick={() => nav(-1)}
                      className="fe-5 fw-bold"
                      style={{ cursor: "pointer" }}
                    >
                      <span className="bi bi-chevron-left"></span> Back
                    </div>
                    <h1 className="mb-0">KSC</h1>
                    <p>Knowledge Source Configuration</p>
                  </div>
                </Col>
                <Col md="6" sm="12">
                  <div className="d-flex justify-content-md-end justify-content-sm-start ">
                    <h2 className="">KSC ID: {owner.ksc_id}</h2>
                  </div>
                </Col>
              </Row>

              <div>
                <Row className="align-items-center mb-2">
                  <Col sm="12" md="12" lg="12" className="mt-2">
                    <Row>
                      <Col sm="6" md="6" lg="4">
                        <span>
                          <div className="fw-bold">Name:</div>
                          <input
                            type="text"
                            readOnly={!editableFields.name}
                            defaultValue={owner.name || ""}
                            className="white-background-input"
                            style={{
                              background: "transparent",
                              border: editableFields.name
                                ? "1px solid #ccc"
                                : "none",
                              outline: "none",
                            }}
                            onChange={(e) =>
                              editableFields.name && handleChange(e, "name")
                            }
                          />
                          {editableFields.name ? (
                            <React.Fragment>
                              <i
                                className="fa fa-save"
                                onClick={() => handleSaveClick("name")}
                                style={{
                                  cursor: "pointer",
                                  marginLeft: "5px",
                                }}
                              ></i>
                            </React.Fragment>
                          ) : (
                            showEditButton && (
                              <i
                                className="fa fa-pen-to-square"
                                onClick={() => handleEditClick("name")}
                                style={{
                                  cursor: "pointer",
                                  marginLeft: "5px",
                                }}
                              ></i>
                            )
                          )}
                        </span>
                      </Col>
                      <Col sm="6" md="6" lg="4">
                        <div>
                          <div className="fw-bold">Description:</div>
                          <input
                            type="text"
                            readOnly={!editableFields.description}
                            defaultValue={owner.description || ""}
                            className="white-background-input"
                            style={{
                              border: editableFields.description
                                ? "1px solid #ccc"
                                : "none",
                              outline: "none",
                              background: "transparent",
                            }}
                            onChange={(e) =>
                              editableFields.description &&
                              handleChange(e, "description")
                            }
                          />
                          {editableFields.description ? (
                            <React.Fragment>
                              <i
                                className="fa fa-save"
                                onClick={() => handleSaveClick("description")}
                                style={{
                                  cursor: "pointer",
                                  marginLeft: "5px",
                                }}
                              ></i>
                            </React.Fragment>
                          ) : (
                            showEditButton && (
                              <i
                                className="fa fa-pen-to-square"
                                onClick={() => handleEditClick("description")}
                                style={{
                                  cursor: "pointer",
                                  marginLeft: "5px",
                                }}
                              ></i>
                            )
                          )}
                        </div>
                      </Col>
                      <Col sm="6" md="6" lg="2">
                        <span>
                          <div className="fw-bold">Confidential:</div>
                          <div className="d-flex justify-content-between align-items-center">
                            <input
                              type="checkbox"
                              readOnly={!editableFields.confidential}
                              checked={newConfidential}
                              className="white-background-input"
                              style={{
                                border: editableFields.confidential
                                  ? "1px solid #ccc"
                                  : "none",
                                outline: "none",
                              }}
                              onChange={(e) => {
                                if (editableFields.confidential) {
                                  handleChange(e, "confidential");
                                  handleNewConfidential(e);
                                }
                              }}
                            />
                            {editableFields.confidential ? (
                              <React.Fragment>
                                <i
                                  className="fa fa-save"
                                  onClick={() =>
                                    handleSaveClick("confidential")
                                  }
                                  style={{
                                    cursor: "pointer",
                                    marginRight: "15px",
                                  }}
                                ></i>
                              </React.Fragment>
                            ) : (
                              showEditButton && (
                                <i
                                  className="fa fa-pen-to-square"
                                  onClick={() =>
                                    handleEditClick("confidential")
                                  }
                                  style={{
                                    cursor: "pointer",
                                    marginRight: "15px",
                                  }}
                                ></i>
                              )
                            )}
                          </div>
                        </span>
                      </Col>
                      <Col sm="6" md="6" lg="2">
                        <span>
                          <div className="fw-bold">Owner Account ID:</div>
                          <div className="d-flex justify-content-between align-items-center">
                            {editableFields?.owner_account_id ? (
                              <input
                                type="number"
                                defaultValue={owner.owner_account_id || ""}
                                className="white-background-input w-100"
                                style={{
                                  border: editableFields.owner_account_id
                                    ? "1px solid #ccc"
                                    : "none",
                                  outline: "none",
                                }}
                                onChange={(e) =>
                                  handleChange(e, "owner_account_id")
                                }
                              />
                            ) : (
                              owner.owner_account_id
                            )}
                            {showEditButton &&
                              !editableFields.owner_account_id && (
                                <i
                                  className="fa fa-pen-to-square"
                                  onClick={() =>
                                    handleEditClick("owner_account_id")
                                  }
                                  style={{
                                    cursor: "pointer",
                                    marginRight: "15px",
                                  }}
                                ></i>
                              )}
                            {editableFields.owner_account_id && (
                              <i
                                className="fa fa-save"
                                onClick={() =>
                                  handleSaveClick("owner_account_id")
                                }
                                style={{
                                  cursor: "pointer",
                                  marginRight: "15px",
                                }}
                              ></i>
                            )}
                          </div>
                        </span>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <Accordion className="mb-5">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>
                      <strong>KSs</strong>
                    </Accordion.Header>
                    <Accordion.Body>
                      <div
                        style={{
                          position: "relative",
                          paddingTop: "20px",
                          paddingBottom: "20px",
                        }}
                      >
                        {editModeKs && (
                          <Select
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                width: "20rem",
                              }),
                              menu: (provided) => ({
                                ...provided,
                                width: "20rem",
                              }),
                            }}
                            value={selectedKs}
                            onChange={handleKsChange}
                            options={options}
                            isClearable={true}
                          />
                        )}
                        {showEditButton && !editableKs && (
                          <i
                            className="fa fa-plus-square-o"
                            onClick={handleEditKs}
                            style={{
                              cursor: "pointer",
                              marginLeft: "5px",
                              position: "absolute",
                              right: "0",
                              top: "50%",
                              transform: "translateY(-50%)",
                            }}
                          ></i>
                        )}
                        {editableKs && (
                          <i
                            className="fa fa-save"
                            onClick={handleSaveKs}
                            style={{
                              cursor: "pointer",
                              marginLeft: "5px",
                            }}
                          ></i>
                        )}
                      </div>
                      <Table
                        responsive
                        striped
                        bordered
                        hover
                        className="x-auto hide-table"
                        style={{ minHeight: "106px" }}
                      >
                        <thead>
                          <tr>
                            {column.map((col, index) => (
                              <th key={index}>{col.title}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {data?.map((item, index) => (
                            <tr key={index}>
                              <td>
                                {renderId(item.ks_id ? item.ks_id : 0)}
                              </td>
                              <td>{item.name ? item.name : "Null"}</td>
                              <td>{item.type ? item.type : "Null"}</td>
                              {showEditButton && (
                                <td>
                                  <Button
                                    variant="danger"
                                    onClick={() => handleRemoveKs(item.ks_id)}
                                  >
                                    Remove
                                  </Button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
                {data?.map((item, index) => (
                  <React.Fragment key={index}>
                    <div
                      className="p-3"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "white",
                        borderRadius: "5px",
                      }}
                    >
                      <h4 style={{ marginTop: 0 }}>
                        <strong>Data</strong>
                      </h4>
                      <Row>
                        <Col sm="6" md="6" lg="3">
                          <div>
                            <div className="fw-bold">Name:</div>
                            {item.name}
                          </div>
                        </Col>
                        <Col sm="6" md="6" lg="3">
                          <div>
                            <div className="fw-bold">Description:</div>
                            {item.description}
                          </div>
                        </Col>
                        <Col sm="6" md="6" lg="3">
                          <div>
                            <div className="fw-bold">Type:</div>
                            {item.type}
                          </div>
                        </Col>
                        <Col sm="6" md="6" lg="3">
                          <div>
                            <div className="fw-bold">Ks ID:</div>
                            {renderId(item.ks_id)}
                          </div>
                        </Col>
                      </Row>
                    </div>
                    <div className="">
                      {renderAccordion(
                        "source_details",
                        "Source Details",
                        item
                      )}
                      {renderAccordion("stats", "Stats", item)}
                      {renderAccordion("ept_config", "EPT Config", item)}
                      {renderAccordion("versions", "Versions", item)}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </SweetLayout>
  );
};

export default KscDetailedTable;

